/**
 * CreativeCard — Thumbnail card for a creative in the library grid.
 *
 * Shows video placeholder, status badge, format, objective, and date.
 * Click to select or open detail view.
 */
import React, { useEffect, useState } from 'react';
import { Play, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CREATIVE_STATUSES } from '@/components/deal/mockDealData';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';

function CreativeCard({ creative, onClick, selected = false, proposalCount = 0 }) {
  const status = CREATIVE_STATUSES[creative.status] || CREATIVE_STATUSES.draft;
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const date = creative.createdAt
    ? new Date(creative.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  useEffect(() => {
    setThumbnailFailed(false);
  }, [creative.thumbnailUrl]);

  return (
    <Card
      role="button"
      tabIndex={0}
      className={`cursor-pointer transition-all hover:shadow-md group py-0 gap-0 overflow-hidden ${
        selected ? 'ring-2 ring-coral border-coral' : 'hover:border-coral/30'
      }`}
      onClick={() => onClick?.(creative)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(creative); } }}
      aria-label={`${creative.displayName || 'Spec Ad'} creative${selected ? ', selected' : ''}`}
    >
      <CardContent className="p-0">
        {/* Thumbnail */}
        <div className="aspect-video bg-slate-900 overflow-hidden relative">
          {creative.thumbnailUrl && !thumbnailFailed ? (
            <img
              src={creative.thumbnailUrl}
              alt={creative.displayName || creative.objective || 'Creative preview'}
              loading="lazy"
              className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity"
              onError={() => setThumbnailFailed(true)}
            />
          ) : (
            <CreativePreviewFallback
              compact
              label={creative.status === 'processing' ? 'Preview rendering' : 'Preview unavailable'}
            />
          )}

          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
              <Play className="h-4 w-4 text-coral ml-0.5" />
            </div>
          </div>

          {/* Status badge */}
          <Badge className={`absolute top-2 left-2 text-[10px] px-1.5 py-0 ${status.className} border-0`}>
            {status.label}
          </Badge>

          {/* Format badge */}
          {creative.format && (
            <Badge className="absolute top-2 right-2 bg-black/60 text-white border-0 text-[10px] px-1.5 py-0">
              {creative.format === 'ctv_16_9' ? '16:9' : creative.format === 'social_9_16' ? '9:16' : '1:1'}
            </Badge>
          )}
        </div>

        {/* Meta */}
        <div className="p-2.5 space-y-0.5">
          <p className="text-sm font-medium text-foreground truncate">
            {creative.displayName || 'Spec Ad'}
          </p>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {creative.duration ? `${creative.duration}s` : '15s'}
              {creative.tone && creative.tone !== 'auto' ? ` · ${creative.tone}` : ''}
            </p>
            <div className="flex items-center gap-2">
              {proposalCount > 0 && (
                <span className="flex items-center gap-0.5 text-[10px] text-teal font-medium">
                  <FileText className="h-2.5 w-2.5" />
                  {proposalCount}
                </span>
              )}
              {date && (
                <p className="text-xs text-muted-foreground">{date}</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default React.memo(CreativeCard);
